import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { getPEOVerificationStatus } from '@/services/auth.service';
import { useAdminStatus } from '@/hooks/useAdminStatus';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole
}) => {
  const { isAuthenticated, isLoading, user, userRole } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useAdminStatus();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isVerificationChecking, setIsVerificationChecking] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [verificationComplete, setVerificationComplete] = useState(false);
  
  useEffect(() => {

    // Check PEO verification status if needed
    const checkPEOVerification = async () => {
      if (isAuthenticated && userRole === 'peo' && user?.id) {
        try {
          console.log('ProtectedRoute: Starting PEO verification check');
          setIsVerificationChecking(true);
          const status = await getPEOVerificationStatus(user.id);
          setVerificationStatus(status);
          console.log('ProtectedRoute: PEO verification status:', status);
        } catch (err) {
          console.error('Error checking PEO verification:', err);
        } finally {
          setIsVerificationChecking(false);
          setVerificationComplete(true);
        }
      } else {
        // If not a PEO user, we don't need to check verification
        setVerificationComplete(true);
      }
    };
    
    if (!isLoading && isAuthenticated && userRole === 'peo') {
      checkPEOVerification();
    } else if (!isLoading) {
      // Mark verification as complete for non-PEO users
      setVerificationComplete(true);
    }
    
    // Detailed debug checks
    if (!isLoading) {
      if (!isAuthenticated) {
        console.log('ProtectedRoute: Not authenticated, redirecting to login');
      }
      
      if (isAuthenticated && requiredRole === 'super_admin' && !isSuperAdmin) {
        console.log('ProtectedRoute: User is not super admin, access denied');
      }
      
      if (isAuthenticated && requiredRole === 'peo' && userRole !== 'peo') {
        console.log('ProtectedRoute: User is not a PEO provider, access denied');
      }
      
      if (isAuthenticated) {
        console.log('ProtectedRoute: User authenticated, role:', userRole);
      }
    }
  }, [isLoading, isAuthenticated, user, userRole, location.pathname, requiredRole, isSuperAdmin]);
  
  // Show loader while checking verification status or loading auth
  const showLoader = isLoading || isVerificationChecking || !verificationComplete || isAdminLoading;
  
  if (showLoader) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-lg">Verifying authentication...</p>
        <p className="text-sm text-muted-foreground mt-2">Please wait while we check your credentials</p>
      </div>
    );
  }
  
  // If there was an error in authentication
  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }
  
  // If user is not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('ProtectedRoute: Redirecting to login');
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  // Check for specific role requirements
  if (requiredRole === 'super_admin') {
    if (!isSuperAdmin) {
      console.log('ProtectedRoute: Access denied - not super admin');
      return <Navigate to="/dashboard" replace />;
    }
  } else if (requiredRole === 'peo') {
    if (userRole !== 'peo') {
      console.log('ProtectedRoute: Access denied - not PEO provider');
      return <Navigate to="/dashboard" replace />;
    }
    
    // Only redirect to the restricted page if trying to access the dashboard
    // and the verification status is not approved
    if (location.pathname === '/peo/dashboard' && verificationStatus !== 'approved') {
      console.log('ProtectedRoute: PEO not verified, redirecting to restricted page');
      return <Navigate to="/peo/restricted" replace />;
    }
  }
  
  // If all checks pass, render the protected content
  console.log('ProtectedRoute: Access granted');
  return <>{children}</>;
};

export default ProtectedRoute;
